import { useState } from 'react';
import { executeLogin } from 'utils/Login';
import { TimekeeperService } from 'services/TimekeeperService';
import { CONFIG_SYSTEM_STORAGE_KEY } from 'repository/timekeeper/ConfigSystemRepo';

type Translator = (key: string) => string;

type LoginFieldState = {
    value: string;
    error: boolean;
    helperText: string;
};

type PendingCredentials = {
    email: string;
    password: string;
};

type LoginResponse = {
    access_token: string;
    permissions: unknown;
    token_type: string;
    expires_in: number | string;
    refresh_token?: string;
    refresh_expires_in?: number | string;
};

type LoginFormParams = {
    setAuthenticated: (value: boolean) => void;
    t: Translator;
};

type SubmitLoginParams = {
    event?: { preventDefault?: () => void };
    email?: string;
    password?: string;
    otp?: string;
};

const toExpiryTimestamp = (expiresInSeconds: number | string): string | null => {
    const seconds = Number(expiresInSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return null;
    }

    return String(Date.now() + Math.floor(seconds * 1000));
};

export const useLoginForm = ({ setAuthenticated, t }: LoginFormParams) => {
    const [email, setEmail] = useState<LoginFieldState>({
        value: '',
        error: false,
        helperText: '',
    });
    const [password, setPassword] = useState<LoginFieldState>({
        value: '',
        error: false,
        helperText: '',
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [otpModalOpen, setOtpModalOpen] = useState(false);
    const [otpValue, setOtpValue] = useState('');
    const [otpError, setOtpError] = useState('');
    const [pendingCredentials, setPendingCredentials] = useState<PendingCredentials | null>(null);

    const persistSession = (response: LoginResponse) => {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('permissions', JSON.stringify(response.permissions));
        localStorage.setItem('token_type', response.token_type);
        localStorage.setItem('expires_in', String(response.expires_in));

        const expiresAt = toExpiryTimestamp(response.expires_in);
        if (expiresAt) {
            localStorage.setItem('expires_at', expiresAt);
        }

        if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
        }

        if (response.refresh_expires_in) {
            localStorage.setItem('refresh_expires_in', String(response.refresh_expires_in));
            const refreshExpiresAt = toExpiryTimestamp(response.refresh_expires_in);
            if (refreshExpiresAt) {
                localStorage.setItem('refresh_expires_at', refreshExpiresAt);
            }
        }

        sessionStorage.setItem('authenticated', 'true');
        setAuthenticated(true);
        setOtpModalOpen(false);
        setOtpValue('');
        setOtpError('');
        setPendingCredentials(null);
    };

    const fetchAndStoreConfigSystem = async () => {
        try {
            const tkService = new TimekeeperService();
            const configs = await tkService.config_system.getConfigSystem();
            localStorage.setItem(CONFIG_SYSTEM_STORAGE_KEY, JSON.stringify(configs));
        } catch (error) {
            console.warn('No se pudo cargar la configuración del sistema:', error);
        }
    };

    const submitLogin = async ({
        event,
        email: providedEmail,
        password: providedPassword,
        otp: providedOtp,
    }: SubmitLoginParams = {}): Promise<boolean> => {
        if (event?.preventDefault) {
            event.preventDefault();
        }

        const currentEmail = (providedEmail ?? email.value).trim();
        const currentPassword = providedPassword ?? password.value;
        const otp = (providedOtp ?? otpValue).trim() || undefined;

        setLoading(true);
        try {
            localStorage.setItem('username', currentEmail);
            const response = (await executeLogin(currentEmail, currentPassword, otp)) as LoginResponse;
            persistSession(response);
            fetchAndStoreConfigSystem();
            return true;
        } catch (error: unknown) {
            localStorage.removeItem('username');
            const typedError = error as {
                response?: {
                    status?: number;
                    data?: {
                        error_code?: string;
                    };
                };
            };

            const status = typedError?.response?.status;
            const errorCode = typedError?.response?.data?.error_code;

            if (status === 403 && errorCode === 'mfa_required') {
                setPendingCredentials({
                    email: currentEmail,
                    password: currentPassword,
                });
                setOtpModalOpen(true);
                setOtpError('');
                return false;
            }

            if (status === 400) {
                setOtpError(t('sign_on.form.invalid_otp') || 'OTP incorrecto');
                return false;
            }

            console.error(error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const closeOtpModal = () => {
        setOtpModalOpen(false);
        setOtpValue('');
        setOtpError('');
    };

    const handleOtpSubmit = async (): Promise<boolean> => {
        if (!pendingCredentials) {
            return false;
        }

        if (!otpValue.trim()) {
            setOtpError(t('sign_on.form.otp_required') || 'OTP requerido');
            return false;
        }

        setOtpError('');
        return submitLogin({
            email: pendingCredentials.email,
            password: pendingCredentials.password,
            otp: otpValue,
        });
    };

    return {
        email,
        setEmail,
        password,
        setPassword,
        loading,
        showPassword,
        setShowPassword,
        otpModalOpen,
        otpValue,
        setOtpValue,
        otpError,
        setOtpError,
        submitLogin,
        closeOtpModal,
        handleOtpSubmit,
    };
};